/**
 * Informal groups (web) — the G2 group-lead / group-member self-service screen.
 *
 * Web port of apps/mobile/app/groups/{index,create,invite}.tsx, collapsed onto a
 * single route. Reads GET /groups/mine and branches:
 *
 *   NOT IN A GROUP (404 / no data):
 *     A "Start an informal group" card — name field + a REQUIRED POPIA disclosure
 *     checkbox (the lead vouches for members but is NOT each member's Information
 *     Officer). On create → POST /groups → the caller's role flips
 *     farmer → group_lead server-side, so we refresh the session token and
 *     hard-navigate to /groups (the established hard-nav pattern — see signOut in
 *     _app.tsx) so the whole app re-renders with the new role.
 *
 *   IN A GROUP:
 *     Group header (name + member count) + a roster of members. The LEAD can
 *     invite members (attach an existing independent farmer, or create a
 *     claimable account), remove non-lead members, and dissolve the group (only
 *     when sole member — the server's 400 message surfaces otherwise; dissolving
 *     reverts to farmer → refresh + hard-nav home). A plain MEMBER can leave.
 *
 * The lead-only endpoints (invite/remove/dissolve) 403 until the JWT carries the
 * refreshed role, hence the refresh-then-hard-nav after create/dissolve.
 *
 * Web is ONLINE-ONLY — plain react-query, no SQLite cache / offline banners.
 *
 * Endpoints: GET /groups/mine, POST /groups, POST /groups/members/invite,
 * DELETE /groups/members/:id, POST /groups/leave, POST /groups/dissolve.
 * Generated orval client src/api/generated/groups/groups (TD-070).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItem,
  Stack,
  Text,
  TextField,
} from '@fse/ui';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims, refreshSession } from '../api/client';
import {
  groupsControllerCreateGroup,
  groupsControllerDissolveGroup,
  groupsControllerGetMine,
  groupsControllerInviteMember,
  groupsControllerLeaveGroup,
  groupsControllerRemoveMember,
} from '../api/generated/groups/groups';
import type { GroupDetailDto, GroupMemberDto } from '../api/generated/models';

const MIN_NAME_LEN = 2;
const MAX_NAME_LEN = 120;

/** Pull a human-readable message out of an unknown thrown value. */
function apiMessage(err: unknown): string {
  if (err instanceof FseApiError) return err.message;
  return String(err);
}

const GROUPS_MINE_KEY = ['groups-mine'] as const;

// ---------------------------------------------------------------------------
// NOT IN A GROUP — create flow
// ---------------------------------------------------------------------------

function CreateGroupCard({ t }: { t: TFunction }) {
  const [name, setName] = useState('');
  const [accepted, setAccepted] = useState(false);

  const createMutation = useMutation({
    mutationFn: (groupName: string) =>
      groupsControllerCreateGroup({ name: groupName, disclosureAccepted: true }),
    onSuccess: async () => {
      // The caller's role flipped farmer → group_lead server-side. Refresh the
      // session so the new role claim is live, then hard-navigate so the whole
      // app re-renders with the new role (the lead-only endpoints 403 otherwise).
      await refreshSession().catch(() => {});
      window.location.assign('/groups');
    },
  });

  const trimmed = name.trim();
  const nameValid = trimmed.length >= MIN_NAME_LEN && trimmed.length <= MAX_NAME_LEN;
  const canSubmit = nameValid && accepted && !createMutation.isPending;

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('groups.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('groups.create.subtitle')}
          </Text>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Stack spacing={2.5}>
            <Box>
              <Text variant="title-medium" as="h3">
                {t('groups.create.heading')}
              </Text>
              <Box sx={{ mt: 0.5 }}>
                <Text variant="body-medium" color="text.secondary">
                  {t('groups.create.body')}
                </Text>
              </Box>
            </Box>

            <TextField
              label={t('groups.create.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_NAME_LEN } }}
              disabled={createMutation.isPending}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  disabled={createMutation.isPending}
                  sx={{ alignSelf: 'flex-start', mt: -0.5 }}
                />
              }
              label={
                <Text variant="body-medium" color="text.primary">
                  {t('groups.create.disclosure')}
                </Text>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />

            {createMutation.isError ? (
              <Alert severity="error">
                {t('groups.create.error', { error: apiMessage(createMutation.error) })}
              </Alert>
            ) : null}

            <Box>
              <Button
                variant="contained"
                onClick={() => createMutation.mutate(trimmed)}
                disabled={!canSubmit}
                startIcon={
                  createMutation.isPending ? <CircularProgress size={16} /> : undefined
                }
              >
                {t('groups.create.cta')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// IN A GROUP — dashboard
// ---------------------------------------------------------------------------

function InviteDialog({
  open,
  onClose,
  t,
}: {
  open: boolean;
  onClose: () => void;
  t: TFunction;
}) {
  const queryClient = useQueryClient();
  const [phoneE164, setPhoneE164] = useState('');
  // Holds the success banner once an invite resolves (attached / invited).
  const [outcomeMsg, setOutcomeMsg] = useState<string | null>(null);
  // Share-a-link path: a copyable web signup link attributed to the lead
  // (mirrors the mobile fse://signup?ref= deep link; web signup reads ?ref).
  const [copied, setCopied] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: (body: { phoneE164: string; displayName: string }) =>
      groupsControllerInviteMember(body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: GROUPS_MINE_KEY });
      setOutcomeMsg(
        res.outcome === 'attached'
          ? t('groups.invite.attached', { name: res.displayName })
          : t('groups.invite.invited', { name: res.displayName }),
      );
      setPhoneE164('');
    },
  });

  const close = () => {
    if (inviteMutation.isPending) return;
    setPhoneE164('');
    setOutcomeMsg(null);
    inviteMutation.reset();
    onClose();
  };

  const canSubmit = phoneE164.trim().length > 0 && !inviteMutation.isPending;

  const sub = readAccessTokenClaims()?.sub;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const inviteLink = sub ? `${origin}/signup?ref=${encodeURIComponent(sub)}` : '';
  async function onCopyLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard denied — the read-only field still allows manual copy */
    }
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('groups.invite.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('groups.invite.body')}</DialogContentText>
        <Stack spacing={2}>
          <Box>
            <TextField
              label={t('groups.invite.phoneLabel')}
              value={phoneE164}
              onChange={(e) => setPhoneE164(e.target.value)}
              placeholder={t('groups.invite.phonePlaceholder')}
              disabled={inviteMutation.isPending}
              fullWidth
            />
            <Box sx={{ mt: 0.5 }}>
              <Text variant="body-small" color="text.secondary">
                {t('groups.invite.nameHint')}
              </Text>
            </Box>
          </Box>
          {outcomeMsg ? <Alert severity="success">{outcomeMsg}</Alert> : null}
          {inviteMutation.isError ? (
            <Alert severity="error">
              {t('groups.invite.error', { error: apiMessage(inviteMutation.error) })}
            </Alert>
          ) : null}
        </Stack>

        {/* Or share a join link — a recruit signs up via the link (attributed to
            the lead), then the lead adds their number above. */}
        {inviteLink ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              <Text variant="title-small" color="text.primary">
                {t('groups.invite.shareSectionTitle')}
              </Text>
              <Text variant="body-small" color="text.secondary">
                {t('groups.invite.shareSectionBody')}
              </Text>
              <TextField
                label={t('groups.invite.shareLinkLabel')}
                value={inviteLink}
                fullWidth
                slotProps={{
                  input: { readOnly: true },
                  htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 } },
                }}
                onFocus={(e) => e.currentTarget.select()}
              />
              <Box>
                <Button variant="outlined" onClick={onCopyLink}>
                  {copied ? t('groups.invite.shareCopied') : t('groups.invite.shareCopy')}
                </Button>
              </Box>
            </Stack>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={close} disabled={inviteMutation.isPending}>
          {t('groups.invite.close')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            // The invitee no longer provides a name at invite time: an existing
            // member keeps their own name; a claimable account sets their real
            // name when they claim. The generated body still types displayName as
            // required, so we send the phone as a placeholder value (the backend
            // ignores it for an existing member and uses it as the placeholder
            // for a claimable until they claim).
            const phone = phoneE164.trim();
            inviteMutation.mutate({ phoneE164: phone, displayName: phone });
          }}
          disabled={!canSubmit}
          startIcon={inviteMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('groups.invite.cta')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MemberRow({
  member,
  isSelf,
  canRemove,
  onRemove,
  disabled,
  t,
}: {
  member: GroupMemberDto;
  isSelf: boolean;
  canRemove: boolean;
  onRemove: () => void;
  disabled: boolean;
  t: TFunction;
}) {
  return (
    <ListItem
      leadingIcon="account_circle"
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Text variant="body-large" color="text.primary">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {member.displayName}
            </Box>
          </Text>
          {isSelf ? (
            <Text variant="label-small" color="text.secondary">
              {t('groups.dashboard.you')}
            </Text>
          ) : null}
        </Box>
      }
      supportingText={
        <Text variant="body-small" color="text.secondary">
          {member.phoneE164}
        </Text>
      }
      trailing={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {member.isLead ? (
            <Chip kind="assist" color="success" label={t('groups.dashboard.leadTag')} />
          ) : null}
          <Chip
            kind="assist"
            color={member.claimed ? 'success' : 'neutral'}
            label={
              member.claimed
                ? t('groups.dashboard.claimed')
                : t('groups.dashboard.notClaimed')
            }
          />
          {canRemove ? (
            <Button variant="text" size="small" onClick={onRemove} disabled={disabled}>
              {t('groups.dashboard.remove')}
            </Button>
          ) : null}
        </Box>
      }
    />
  );
}

function GroupDashboard({
  group,
  sub,
  t,
}: {
  group: GroupDetailDto;
  sub: string;
  t: TFunction;
}) {
  const queryClient = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<GroupMemberDto | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [dissolveOpen, setDissolveOpen] = useState(false);

  const removeMutation = useMutation({
    mutationFn: (userId: string) => groupsControllerRemoveMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GROUPS_MINE_KEY });
      setRemoveTarget(null);
    },
  });

  const leaveMutation = useMutation({
    mutationFn: () => groupsControllerLeaveGroup(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GROUPS_MINE_KEY });
      setLeaveOpen(false);
    },
  });

  const dissolveMutation = useMutation({
    mutationFn: () => groupsControllerDissolveGroup(),
    onSuccess: async () => {
      // Role reverted group_lead → farmer server-side. Refresh the session so
      // the new claim is live, then hard-navigate home so the whole app
      // re-renders with the demoted role.
      await refreshSession().catch(() => {});
      window.location.assign('/home');
    },
  });

  const busy =
    removeMutation.isPending || leaveMutation.isPending || dissolveMutation.isPending;

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Text variant="body-medium" color="text.secondary">
            {group.isLead ? t('groups.dashboard.youLead') : t('groups.dashboard.memberOf')}
          </Text>
          <Text variant="headline-small" as="h2">
            {group.name}
          </Text>
          <Box sx={{ mt: 0.5 }}>
            <Text variant="body-medium" color="text.secondary">
              {t('groups.dashboard.memberCount', { count: group.memberCount })}
            </Text>
          </Box>
        </Box>
        {group.isLead ? (
          <Button variant="contained" onClick={() => setInviteOpen(true)} disabled={busy}>
            {t('groups.dashboard.invite')}
          </Button>
        ) : null}
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {group.members.map((m) => (
            <MemberRow
              key={m.userId}
              member={m}
              isSelf={m.userId === sub}
              // Lead can remove any non-lead member (not themselves — dissolve).
              canRemove={group.isLead && !m.isLead}
              onRemove={() => setRemoveTarget(m)}
              disabled={busy}
              t={t}
            />
          ))}
          {group.members.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('groups.dashboard.emptyRoster')}
              </Text>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {group.isLead ? (
        <Box>
          <Button variant="outlined" color="error" onClick={() => setDissolveOpen(true)} disabled={busy}>
            {t('groups.dashboard.dissolve')}
          </Button>
          <Box sx={{ mt: 1 }}>
            <Text variant="body-small" color="text.secondary">
              {t('groups.dashboard.dissolveNote')}
            </Text>
          </Box>
        </Box>
      ) : (
        <Box>
          <Button variant="outlined" color="error" onClick={() => setLeaveOpen(true)} disabled={busy}>
            {t('groups.dashboard.leave')}
          </Button>
        </Box>
      )}

      {/* Invite dialog (lead) */}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} t={t} />

      {/* Remove member confirm (lead) */}
      <Dialog
        open={removeTarget != null}
        onClose={() => {
          if (!removeMutation.isPending) setRemoveTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('groups.dashboard.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('groups.dashboard.removeConfirmBody', {
              name: removeTarget?.displayName ?? '',
            })}
          </DialogContentText>
          {removeMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('groups.dashboard.removeError', { error: apiMessage(removeMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setRemoveTarget(null)}
            disabled={removeMutation.isPending}
          >
            {t('groups.dashboard.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => removeTarget && removeMutation.mutate(removeTarget.userId)}
            disabled={removeMutation.isPending}
            startIcon={removeMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('groups.dashboard.confirmRemove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave group confirm (member) */}
      <Dialog
        open={leaveOpen}
        onClose={() => {
          if (!leaveMutation.isPending) setLeaveOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('groups.dashboard.leaveConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('groups.dashboard.leaveConfirmBody', { group: group.name })}
          </DialogContentText>
          {leaveMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('groups.dashboard.leaveError', { error: apiMessage(leaveMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setLeaveOpen(false)}
            disabled={leaveMutation.isPending}
          >
            {t('groups.dashboard.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => leaveMutation.mutate()}
            disabled={leaveMutation.isPending}
            startIcon={leaveMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('groups.dashboard.confirmLeave')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dissolve group confirm (lead) */}
      <Dialog
        open={dissolveOpen}
        onClose={() => {
          if (!dissolveMutation.isPending) setDissolveOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('groups.dashboard.dissolveConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('groups.dashboard.dissolveConfirmBody', { group: group.name })}
          </DialogContentText>
          {dissolveMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {/* The 400 "remove members / settle harvests first" message comes
                  from the server — surface it verbatim. */}
              {t('groups.dashboard.dissolveError', { error: apiMessage(dissolveMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setDissolveOpen(false)}
            disabled={dissolveMutation.isPending}
          >
            {t('groups.dashboard.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => dissolveMutation.mutate()}
            disabled={dissolveMutation.isPending}
            startIcon={dissolveMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('groups.dashboard.confirmDissolve')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

export function GroupsScreen() {
  const { t } = useTranslation();
  const sub = readAccessTokenClaims()?.sub;

  // 404 (not in a group) is the EXPECTED "no group" signal — retry:false so a
  // 404 resolves to the error path quickly, where we render the create flow.
  const q = useQuery({
    queryKey: GROUPS_MINE_KEY,
    queryFn: ({ signal }) => groupsControllerGetMine(signal),
    enabled: !!sub,
    retry: false,
  });

  // Could not derive the caller's identity from the token — surface as an error
  // rather than firing a query we know will be rejected.
  if (!sub) {
    return (
      <Stack spacing={3}>
        <Box>
          <Text variant="headline-small" as="h2">
            {t('groups.title')}
          </Text>
        </Box>
        <Alert severity="error">{t('groups.noIdentity')}</Alert>
      </Stack>
    );
  }

  if (q.isPending) {
    return (
      <Stack spacing={3}>
        <Box>
          <Text variant="headline-small" as="h2">
            {t('groups.title')}
          </Text>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Stack>
    );
  }

  // On error (incl. 404) OR no data → the caller is NOT in a group: offer to
  // start one. Any genuine server error also lands here; the create call will
  // surface its own error if the user tries again.
  if (q.isError || !q.data) {
    return <CreateGroupCard t={t} />;
  }

  return <GroupDashboard group={q.data} sub={sub} t={t} />;
}
